import { createSlice } from '@reduxjs/toolkit';
import { createAppAsyncThunk } from '../createAppAsyncThunk';
import { API_DOWNLOAD } from '../../services';
import type { RootState } from '../store';
import { env } from '../../env';
import { ENDPOINTS } from '../../constants/endPoints';
import { RADRequest } from '../../models/common/RADRequest';
import { isMobileDevice, saveBlobAsFile } from '../../utils/appUtils';
import lang from '../../languages/en.json';

interface SummaryExportState {
  isLoading: boolean;
}

const initialState: SummaryExportState = {
  isLoading: false,
};

// Async thunk to download the chain summary export. Fetches the .xlsx blob, validates the
// response, and writes it to disk using the server-supplied filename. Nothing is persisted
// in state besides the in-flight flag.
export const exportSummary = createAppAsyncThunk(
  'summaryExport/export',
  async (payload: RADRequest) => {
    // Defense-in-depth: the UI hides the trigger on mobile, but the thunk is the chokepoint
    // for any future caller. True enforcement still belongs on the backend.
    if (isMobileDevice()) {
      throw new Error(lang.storeBanner.summaryExportMobileError);
    }

    // The server stamps the filename from the SQL data dates and returns it via
    // Content-Disposition. The fallback uses "Unknown" for the date portion so a missing
    // header is visible to the user rather than silently defaulting to the current month.
    const fallbackFileName = `Store-${payload.storeNumber}-RADSummary-Unknown.xlsx`;

    const { blob, fileName } = await API_DOWNLOAD(
      `${env.RAD_APP_API_URL}${ENDPOINTS.GET_SUMMARY_EXPORT}`,
      payload,
      fallbackFileName,
    );

    // Reject empty bodies and HTML error pages returned with a 200 status —
    // axios does not throw on these, so we'd otherwise save a corrupt file.
    if (!blob || blob.size === 0 || blob.type.includes('text/html')) {
      throw new Error(lang.storeBanner.summaryExportInvalidResponse);
    }

    saveBlobAsFile(blob, fileName);
  },
);

const summaryExportSlice = createSlice({
  name: 'summaryExport',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(exportSummary.pending, state => {
        state.isLoading = true;
      })
      .addCase(exportSummary.fulfilled, state => {
        state.isLoading = false;
      })
      .addCase(exportSummary.rejected, state => {
        state.isLoading = false;
      });
  },
});

// Selectors
export const selectIsSummaryExportLoading = (state: RootState): boolean =>
  state.summaryExport.isLoading;

export default summaryExportSlice.reducer;
